// When more than one promo is applied - e.g. a Can Club discount and a manually
// entered promo code - the OMS will only pick/apply the biggest discount (they
// don't stack). Unlike gatsby-theme-autonomy's promotions utils, these
// functions allow the front-end to retrieve all promos, including those that
// aren't "eligible". This allows us to detect when more than one have been
// applied and make a user aware of this behavior.

import { toUsdCurrency } from '~/utils/formatters'

const orderPromotion = (adjustment) => {
  return (
    adjustment.sourceType === 'Spree::PromotionAction' &&
    adjustment.adjustableType === 'Spree::Order'
  )
}

const lineItemPromotion = (adjustment) => {
  return (
    adjustment.sourceType === 'Spree::PromotionAction' &&
    adjustment.adjustableType === 'Spree::LineItem'
  )
}

const getOrderPromotions = (order) => {
  if (!order.adjustments || order.adjustments.length === 0) return []

  return order.adjustments.filter((a) => orderPromotion(a))
}

const getLineItemPromotions = (order) => {
  if (!order.lineItems || order.lineItems.length === 0) return []

  return order.lineItems.reduce((acc, lineItem) => {
    const eligiblePromotions = lineItem.adjustments.filter((a) =>
      lineItemPromotion(a)
    )
    return acc.concat(eligiblePromotions)
  }, [])
}

const getAllCartPromotions = (order) => {
  if (!order) return []

  const orderPromotions = getOrderPromotions(order)
  const lineItemPromotions = getLineItemPromotions(order)

  const promotions = [...orderPromotions, ...lineItemPromotions]

  const groupedPromos = promotions.reduce(
    (acc, { eligible, label, amount, promotionCode, sourceId }) => {
      // Groups by sourceId rather than promotionCodeId because club membership
      // promos don't have a promotionCodeId. That'll cause issues if a user is
      // a member of both clubs (rare, but possible).
      acc[sourceId] = acc[sourceId] || {
        label,
        eligible,
        amount: 0,
        code: promotionCode ? promotionCode.value : null,
      }
      if (eligible) {
        acc[sourceId].amount += parseFloat(amount)
        acc[sourceId].eligible = true
      }
      return acc
    },
    {}
  )

  return Object.entries(groupedPromos).map(
    ([key, { label, eligible, amount, code }]) => ({
      id: key,
      label,
      eligible,
      amount,
      displayAmount: toUsdCurrency(amount),
      code: code ? code.toUpperCase() : null,
    })
  )
}

const getAllCartPromotionsForDisplay = (promotions) => {
  return (
    promotions
      .filter((p) => p.eligible)
      // @todo Remove after pemanent fix.
      .filter((p) => p.label !== 'Adult Signature Required')
  )
}

export { getAllCartPromotions, getAllCartPromotionsForDisplay }
